/**
 * @description 解析商城库存快照；缺少新契约时保留旧接口库存语义。
 * @param {Object} source 商品、SKU 或购物车扁平行
 * @param {{isAgencyUser?: boolean, legacyQuantity?: unknown}} options 身份与旧字段
 * @returns {Object} 可购买状态与数量上限（未知不等于零库存）
 */
export function resolveInventoryAvailability(source, options = {}) {
  const status = source?.inventoryStatus
  const hasContract = typeof status === 'string' && status.length > 0
  const exempt =
    options.isAgencyUser === true || source?.inventoryExempt === true || status === 'not_applicable'
  const raw = hasContract ? source?.availableQuantity : options.legacyQuantity
  const quantity = raw == null || raw === '' ? null : Number(raw)
  const availableQuantity = Number.isFinite(quantity) && quantity >= 0 ? Math.trunc(quantity) : null
  const updating = !exempt && hasContract && (status !== 'fresh' || availableQuantity === null)
  const soldOut = !exempt && !updating && availableQuantity === 0
  return {
    hasContract,
    status: hasContract ? status : 'legacy',
    availableQuantity,
    snapshotAt: source?.snapshotAt ?? null,
    updating,
    soldOut,
    blocked: updating || soldOut,
    maxQuantity: exempt ? Infinity : updating ? 0 : (availableQuantity ?? Infinity),
    message: updating ? '库存更新中，请稍后重试' : soldOut ? '商品售罄' : '',
  }
}

/** @description 优先当前 SKU 的明确快照；不得用商品总库存覆盖 SKU 的零库存。 */
export function resolveProductInventory(product, sku, options = {}) {
  const source = sku?.inventoryStatus ? sku : product?.inventoryStatus ? product : sku || product
  const legacySku = [
    sku?.actualStock,
    sku?.actualStocks,
    sku?.warehouseStock,
    sku?.availableStock,
    sku?.stocks,
    sku?.stock,
  ].find(
    (value) => value != null && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0
  )
  const legacyQuantity = legacySku ?? product?.totalStocks ?? product?.stocks ?? product?.stock
  return resolveInventoryAvailability(source, { ...options, legacyQuantity })
}

/** @description 仅复制库存契约；调用范围限定商品/购物车，不能覆盖售后可退数量。 */
export function inventorySnapshotFields(source) {
  if (!source?.inventoryStatus) return {}
  return {
    inventoryStatus: source.inventoryStatus,
    availableQuantity: source.availableQuantity ?? null,
    snapshotAt: source.snapshotAt ?? null,
  }
}

/** @description 购物车库存标签；保留购买数量，让用户看到刷新后的不足数量并主动修改。 */
export function resolveCartInventoryStatusLabel(line) {
  const state = resolveInventoryAvailability(line, {
    legacyQuantity: line?.stocks ?? line?.stock,
  })
  if (state.updating) return '库存更新中'
  if (state.soldOut) return '售罄'
  return Number(line?.quantity) > state.maxQuantity ? `仅剩${state.maxQuantity}台` : ''
}

/** @description 确认页按展示顺序查找第一条库存不足明细，与后端错误共用提示格式。 */
export function resolveOrderInventoryError(lines = [], options = {}) {
  for (const line of lines) {
    const state = resolveInventoryAvailability(line, {
      ...options,
      legacyQuantity: line?.stock,
    })
    if (state.updating) return readInventoryError({ code: 'INVENTORY_UNAVAILABLE' })
    if (!state.soldOut && !(Number(line?.quantity) > state.maxQuantity)) continue
    return readInventoryError({
      code: 'INVENTORY_SHORTAGE',
      firstShortage: {
        productName: line?.name || line?.prodName,
        skuName: line?.spec || line?.skuName,
        availableQuantity: state.maxQuantity,
        requestedQuantity: Number(line?.quantity),
      },
    })
  }
  return null
}

/** @description 保留 Axios/业务封装的库存错误明细，兼容 Nest HTTP 异常包装。 */
export function readInventoryError(error) {
  const raw = error?.response?.data ?? error?.raw ?? error
  const candidates = [raw, raw?.data, raw?.error, raw?.message]
  const detail = candidates.find(
    (item) =>
      item &&
      typeof item === 'object' &&
      ['INVENTORY_SHORTAGE', 'INVENTORY_UNAVAILABLE'].includes(item.code)
  )
  if (!detail) return null
  const shortage = detail.firstShortage
  const message =
    detail.code === 'INVENTORY_UNAVAILABLE'
      ? '库存更新中，请稍后重试'
      : shortage
        ? `提交失败：${shortage.productName || '商品'}${shortage.skuName ? `（${shortage.skuName}）` : ''}只剩${shortage.availableQuantity}台，请修改后再操作`
        : detail.message || '提交失败：商品库存不足，请修改后再操作'
  return { code: detail.code, message, firstShortage: shortage ?? null }
}
